import React, { useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import useClickOutside from "@/hooks/useClickOutside";
import useEventEffect from "@/hooks/useEventEffect";

/**
 * Provider component for right-click context menus that handles positioning
 * using showit-wc Menu's native popover functionality
 * @param {Object} props
 * @param {React.ReactNode} props.children - Menu content to render (should be a Menu component)
 * @param {Function} props.onClose - Callback when menu should close
 * @param {Object} props.menuState - Menu state containing mouse coordinates
 * @param {string} [props.className] - CSS class for the menu container
 */
const RightClickMenuProvider = ({
  children,
  onClose,
  menuState,
  className = "right-click-menu",
}) => {
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  // Handle Escape key to close menu
  const handleEscapeKey = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );
  useEventEffect(window, "keydown", handleEscapeKey, !!menuState);

  // Show the menu popover when menuState changes
  useEffect(() => {
    if (!menuState || typeof menuState.mouseX !== "number" || typeof menuState.mouseY !== "number") {
      return;
    }

    const showMenu = async () => {
      const menuElement = menuRef.current;
      const anchorElement = anchorRef.current;

      if (menuElement && anchorElement) {
        // Wait for the component to be ready
        await menuElement.updateComplete;
        // Show the popover anchored to our hidden anchor element
        menuElement.showPopover?.(anchorElement);

        // Focus the menu so it captures keyboard events
        requestAnimationFrame(() => {
          // Focus the first menu item for keyboard navigation
          const firstItem = menuElement.querySelector("si-menu-item:not([divider])");
          if (firstItem) {
            firstItem.focus();
          }
        });
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      showMenu();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [menuState]);

  // Handle click outside to close menu
  useClickOutside(menuRef, onClose, {
    enabled: !!menuState,
    delay: 100,
    listenForContextMenu: true,
  });

  if (!menuState || typeof menuState.mouseX !== "number" || typeof menuState.mouseY !== "number") {
    return null;
  }

  // Clone is necessary because the Menu web component is passed as children,
  // but we need to inject a ref (for click-outside and popover positioning),
  // override closeType to "manual" (prevent the web component's auto-close behavior),
  // and merge CSS classes for styling.
  const menuWithRef = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        ref: menuRef,
        closeType: "manual",
        className: `${child.props.className || ""} ${className}`.trim(),
      });
    }
    return child;
  });

  // Prevent keyboard events from bubbling to transformer/stage
  const handleKeyDown = (event) => {
    const navigationKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter", "Tab", " "];
    if (navigationKeys.includes(event.key)) {
      event.stopPropagation();
      // Prevent default to stop page scrolling
      if (event.key !== "Tab") {
        event.preventDefault();
      }
    }
  };

  const portal = (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onKeyDown={handleKeyDown}>
      {/* Hidden anchor element positioned at mouse coordinates */}
      <div
        ref={anchorRef}
        style={{
          position: "fixed",
          left: menuState.mouseX,
          top: menuState.mouseY,
          width: 0,
          height: 0,
          pointerEvents: "none",
        }}
      />
      {/* Menu component */}
      {menuWithRef}
    </div>
  );

  return ReactDOM.createPortal(portal, document.body);
};

export default RightClickMenuProvider;
